import React, {FC, useContext, useEffect, useState} from "react";
import {Box, BoxProps, Menu, MenuItem, Typography, TypographyProps} from "@mui/material";
import {IconContext} from "@buildwitt/shapeshift-identity";
import {makeStyles} from "tss-react/mui";
import type {IPaginatedResponse, ISentNotification} from "@buildwitt/shapeshift-types";
import type {IUser} from "@buildwitt/shapeshift-types";

/**
 * App2 replacement for shapeshift-common {@link PageBaseProfile}: same UI hooks (menu, avatar slot, bell)
 * but **session comes from props** (`user`, `onLogout`) instead of legacy {@link AuthContext}.
 */
export type App2PageBaseProfileOverrides = {
	profileAvatar?: (props: BoxProps) => JSX.Element;
	editAccountModal?: (props: App2EditAccountModalProps) => JSX.Element;
	notificationBell?: (props: BoxProps) => JSX.Element;
	usernameLabel?: (props: TypographyProps) => JSX.Element;
	menu?: (props: {logoutAction: () => void; accountSettingsAction: () => void}) => JSX.Element[];
};

export type App2EditAccountModalProps = {
	open: boolean;
	onClose: () => void;
};

export type App2PageBaseProfileProps = BoxProps & {
	className?: string;
	disableMenu?: boolean;
	disableAccountSettings?: boolean;
	/** Shapeshift {@link NavBar} passes this for legacy {@link PageBaseProfile}; App2 ignores it (use `componentOverrides.editAccountModal`). */
	editUserDetailsForm?: React.ComponentType<Record<string, unknown>>;
	onNotificationAction?: () => void;
	enableNotifications?: boolean;
	/** Improve session user (OAuth / Strapi-shaped); drives avatar + empty-state behavior. */
	user?: IUser;
	onLogout: () => void;
	componentOverrides?: App2PageBaseProfileOverrides;
	data?: {
		notificationCount?: number;
	};
};

const useStyles = makeStyles()((theme) => ({
	pageBaseProfile: {},
	iconContainer: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		width: "45px",
		height: "45px",
		cursor: "pointer",
		textAlign: "center",
		borderRadius: 100,
		color: "white",
		margin: "16px",
		backgroundColor: theme["navBar"]?.statusprofile?.backgroundColor
	},
	label: {
		display: "flex",
		alignItems: "center"
	},
	bellIconContainer: {
		position: "relative",
		width: "48px",
		cursor: "pointer",
		height: "48px",
		borderRadius: "50px",
		backgroundColor: "rgba(255, 255, 255, 0.1)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginTop: "16px"
	},
	bellIcon: {
		color: "#FFFFFF"
	}
}));

export const App2PageBaseProfile: FC<App2PageBaseProfileProps> = ({
	className,
	disableMenu,
	enableNotifications,
	data,
	onNotificationAction,
	disableAccountSettings,
	componentOverrides,
	user,
	onLogout,
	editUserDetailsForm: _editUserDetailsForm,
	...props
}) => {
	const {classes, cx} = useStyles();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const menuOpen = Boolean(anchorEl);
	const [accountModalOpen, setAccountModalOpen] = useState<boolean>(false);
	const [firstRender, setFirstRender] = useState(true);
	const {icon} = useContext(IconContext);

	useEffect(() => setFirstRender(false), []);

	const notificationsCount = data?.notificationCount ?? 0;

	const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
		if (disableMenu) return;
		setAnchorEl(event.currentTarget);
	};

	const handleClose = (ftc?: () => void): void => {
		ftc?.();
		setAnchorEl(null);
	};

	const avatarProps: BoxProps & {"data-test-id": string} = {
		"data-test-id": "pagebase-profile-avatar",
		className: classes.iconContainer,
		onClick: handleClick
	};

	const editAccountModalProps: App2EditAccountModalProps = {
		open: accountModalOpen,
		onClose: () => setAccountModalOpen(false)
	};

	const usernameLabelProps: TypographyProps & {"data-test-id": string} = {
		"data-test-id": "username-label",
		className: classes.label,
		onClick: handleClick
	};

	const notificationBellProps: BoxProps & {
		"data-test-id": string;
		notifications?: IPaginatedResponse<ISentNotification[]>;
	} = {
		"data-test-id": "notification-bell",
		className: classes.bellIconContainer,
		onClick: onNotificationAction,
		notifications: undefined
	};

	const baseMenuRender = [
		<MenuItem key={1} data-test-id="pagebase-profile-logout-btn" onClick={() => handleClose(onLogout)}>
			Logout
		</MenuItem>
	];
	if (!disableAccountSettings)
		baseMenuRender.push(
			<MenuItem
				key={2}
				data-test-id="pagebase-profile-account-setting-btn"
				onClick={() => handleClose(() => setAccountModalOpen(true))}
			>
				Account Settings
			</MenuItem>
		);

	const logout = onLogout;

	return (
		<Box className={cx(classes.pageBaseProfile, className)} sx={{display: "inline-flex"}} {...props}>
			{enableNotifications &&
				(componentOverrides?.notificationBell?.(notificationBellProps) || (
					<Box {...notificationBellProps}>
						{notificationsCount === 0 &&
							icon("notificationsIcon", {
								className: classes.bellIcon
							})}
						{notificationsCount > 0 &&
							icon("notificationsUnreadIcon", {
								className: classes.bellIcon
							})}
					</Box>
				))}
			{!firstRender && (
				<>
					{user &&
						(componentOverrides?.profileAvatar ? (
							componentOverrides.profileAvatar(avatarProps)
						) : (
							<Box {...avatarProps}>
								{(user?.firstname ?? user?.email ?? "u")?.charAt(0)?.toUpperCase()}
								{(user?.lastname ?? " ")?.charAt(0)?.toUpperCase()}
							</Box>
						))}
					{!user && (
						<p key="logout" onClick={() => logout()}>
							Logout
						</p>
					)}

					{!disableMenu && (
						<Menu
							id="profile-menu"
							anchorEl={anchorEl}
							open={menuOpen}
							onClose={() => handleClose()}
							sx={{marginTop: "30px"}}
							MenuListProps={{
								"aria-labelledby": "basic-button"
							}}
						>
							{componentOverrides?.menu ? (
								componentOverrides.menu({
									logoutAction: () => handleClose(logout),
									accountSettingsAction: () => handleClose(() => setAccountModalOpen(true))
								})
							) : (
								baseMenuRender
							)}
						</Menu>
					)}
					{componentOverrides?.usernameLabel?.(usernameLabelProps) || (
						<Typography {...usernameLabelProps}>{user?.firstname ?? user?.email ?? "user"}</Typography>
					)}
				</>
			)}
			{componentOverrides?.editAccountModal?.(editAccountModalProps)}
		</Box>
	);
};

export default App2PageBaseProfile;
